import React, { useEffect, useState } from "react";

// Modules
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useSnackbar } from "notistack";

// App
import { apiEndpoints, serverUrl } from "../../config";
import { getOnlineNode } from "../../core/getNode";
import { postNode } from "../../core/postNode";
import PageHeader from "../../partials/pageHeader";

// UI components
import { Col, Container, Row } from "react-bootstrap";
import {
  Box,
  Button,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import StorefrontIcon from "@mui/icons-material/StorefrontOutlined";

// Single-line input sizing, mirrors addPresentation.js.
const inputSx = {
  "& .MuiInputBase-input": {
    height: 28,
  },
};

export default function AddSalesRoom() {
  const [title, setTitle] = useState("");
  const [company, setCompany] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [templates, setTemplates] = useState([]);

  const user = useSelector((state) => state).authReducer.user;
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    getOnlineNode(`api/v3/sales-room-templates?_format=json`)
      .then((response) => {
        setTemplates(response.data.rows || []);
      })
      .catch(() => {
        // No templates is fine — the blank form still works.
      });
  }, []);

  const createBlank = () => {
    if (!title.trim()) {
      enqueueSnackbar("Please enter a room name", { variant: "warning" });
      return;
    }

    setIsSaving(true);

    const data = {
      title: title.trim(),
      field_company: company.trim(),
      room_status: "draft",
    };

    postNode(apiEndpoints.salesRooms, data, user.csrf_token)
      .then((response) => {
        if (response.status === 201 && response.data?.id) {
          navigate(`/sales-rooms/edit/${response.data.id}`);
        } else {
          setIsSaving(false);
          enqueueSnackbar("Unexpected response while creating the room", {
            variant: "error",
          });
        }
      })
      .catch(() => {
        setIsSaving(false);
        enqueueSnackbar("Failed to create the sales room", {
          variant: "error",
        });
      });
  };

  const createFromTemplate = (template) => {
    if (isSaving) {
      return;
    }
    setIsSaving(true);

    const data = {};
    if (title.trim()) {
      data.title = title.trim();
    }

    postNode(
      `${apiEndpoints.salesRooms}/${template.id}/duplicate`,
      data,
      user.csrf_token
    )
      .then((response) => {
        if (response.status === 201 && response.data?.id) {
          navigate(`/sales-rooms/edit/${response.data.id}`);
        } else {
          setIsSaving(false);
          enqueueSnackbar("Unexpected response while creating the room", {
            variant: "error",
          });
        }
      })
      .catch(() => {
        setIsSaving(false);
        enqueueSnackbar("Failed to create a room from this template", {
          variant: "error",
        });
      });
  };

  return (
    <main className="add-sales-room sales-rooms screen">
      <PageHeader pageName="Create a Sales Room" filters={false} />

      <div className="ps-modal sales-room-settings-modal">
        <div className="ps-modal-content">
          <Container>
            <Row>
              <Col xs={12} md={6}>
                <Box className="ls">
                  <div className="form-group">
                    <label htmlFor="room-title">Room name*</label>
                    <TextField
                      required
                      id="room-title"
                      variant="outlined"
                      fullWidth
                      size="small"
                      autoFocus
                      value={title}
                      sx={inputSx}
                      placeholder="e.g. Acme Corp – Q2 Proposal"
                      onInput={(event) => {
                        setTitle(event.target.value);
                      }}
                    />
                  </div>
                </Box>
              </Col>
              <Col xs={12} md={6}>
                <Box className="rs">
                  <div className="form-group">
                    <label htmlFor="room-company">Company</label>
                    <TextField
                      id="room-company"
                      variant="outlined"
                      fullWidth
                      size="small"
                      value={company}
                      sx={inputSx}
                      placeholder="Prospect company name"
                      onInput={(event) => {
                        setCompany(event.target.value);
                      }}
                    />
                  </div>
                </Box>
              </Col>
            </Row>

            <Row>
              <Col xs={12}>
                <Stack
                  direction="row"
                  justifyContent="flex-end"
                  alignItems="center"
                  spacing={1}
                >
                  <Button
                    variant="outlined"
                    sx={{
                      backgroundColor: "primary.contrastText",
                      textTransform: "none",
                      marginRight: "10px",
                    }}
                    className="cancel"
                    onClick={() => {
                      navigate("/sales-rooms");
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    disabled={isSaving || title.trim() === ""}
                    variant="contained"
                    color="secondary"
                    className="action"
                    sx={{
                      textTransform: "none",
                    }}
                    onClick={() => {
                      createBlank();
                    }}
                  >
                    {isSaving ? <CircularProgress size={20} /> : "Create room"}
                  </Button>
                </Stack>
              </Col>
            </Row>

            {templates.length > 0 && (
              <Row className="mt-4">
                <Col xs={12}>
                  <Typography owner="admin" variant="h6" className="ss-subtitle">
                    Or start from a template
                  </Typography>
                  <Typography owner="admin" variant="body2" className="mb-3">
                    Copies the template's sections, content, branding and plan
                    into a new draft room
                    {title.trim() ? ` named "${title.trim()}"` : ""}.
                  </Typography>
                </Col>
                {templates.map((template) => (
                  <Col xs={12} sm={6} lg={3} key={`template-${template.id}`}>
                    <div
                      className="sales-room-template-card"
                      role="button"
                      tabIndex={0}
                      style={
                        template.field_branding_colour
                          ? { "--dsr-accent": template.field_branding_colour }
                          : undefined
                      }
                      onClick={() => createFromTemplate(template)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          createFromTemplate(template);
                        }
                      }}
                    >
                      <div className="sales-room-template-card__head">
                        {template.field_branding_logo ? (
                          <img
                            src={`${serverUrl}${template.field_branding_logo}`}
                            alt={template.title}
                          />
                        ) : (
                          <StorefrontIcon />
                        )}
                      </div>
                      <div className="sales-room-template-card__body">
                        <Typography owner="admin" variant="body1" weight="medium">
                          {template.title}
                        </Typography>
                        <Typography owner="admin" variant="subtitle2">
                          {template.sections_count} sections ·{" "}
                          {template.items_count} items · by{" "}
                          {template.author?.name || "unknown"}
                        </Typography>
                      </div>
                    </div>
                  </Col>
                ))}
              </Row>
            )}
          </Container>
        </div>
      </div>
    </main>
  );
}
